import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { CoreService } from './core.service';
import { GeocodingService } from './geocoding.service';
import { WeatherContextNormalizerService } from './weather-context-normalizer.service';
import { WeatherDecisionService } from './weather-decision.service';
import { WeatherService } from './weather.service';
import {
  WeatherBonusDecisionRequest,
  WeatherBonusFrontendResult,
  WeatherOcrContext
} from '../models/weather-bonus.models';

@Injectable({
  providedIn: 'root'
})
export class WeatherBonusOrchestratorService {
  constructor(
    private authService: AuthService,
    private coreService: CoreService,
    private normalizer: WeatherContextNormalizerService,
    private geocodingService: GeocodingService,
    private weatherService: WeatherService,
    private decisionService: WeatherDecisionService
  ) {}

  async applyFromUploadResult(uploadResult: any): Promise<WeatherBonusFrontendResult> {
    const rawContext = uploadResult && uploadResult.weatherOcrContext as WeatherOcrContext;
    if (!uploadResult || !uploadResult.isapproved || !rawContext || !rawContext.hasSufficientContext) {
      return {
        status: 'skipped',
        reason: 'missing_weather_context',
        debug: {
          stage: 'missing_weather_context',
          ocrContext: rawContext
        }
      };
    }

    try {
      const context = this.normalizer.normalize(rawContext);
      const addressCandidates = this.normalizer.buildAddressCandidates(context);
      const locationResolution = await this.geocodingService.resolve(context);
      if (locationResolution.resolutionLevel === 'unresolved') {
        return {
          status: 'unresolved',
          reason: 'geocoding_unresolved',
          debug: {
            stage: 'geocoding_unresolved',
            ocrContext: context,
            addressCandidates,
            geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
            geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
            locationResolution
          }
        };
      }

      const weatherLookup = await this.weatherService.fetchHourlyWeather(context, locationResolution);
      if (!weatherLookup) {
        return {
          status: 'unresolved',
          reason: 'weather_unresolved',
          debug: {
            stage: 'weather_unresolved',
            ocrContext: context,
            addressCandidates,
            geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
            geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
            locationResolution,
            weatherRawResponse: this.weatherService.lastDebugResponse,
            weatherLookup: null
          }
        };
      }

      const decision = this.decisionService.evaluate(weatherLookup);
      const purchaseLocalDate = new Date(context.purchaseLocalDateTime);
      const requestPayload: WeatherBonusDecisionRequest = {
        receiptPartitionKey: context.receiptPartitionKey,
        receiptRowKey: context.receiptRowKey,
        userId: this.authService.getUserId(),
        ocrContext: {
          merchantName: context.merchantName,
          streetAddress: context.streetAddress,
          postalCode: context.postalCode,
          city: context.city,
          country: context.country || 'Sweden',
          purchaseLocalDateTime: context.purchaseLocalDateTime,
          purchaseUtcDateTime: purchaseLocalDate.toISOString(),
          timezone: context.timezone || 'Europe/Stockholm'
        },
        locationResolution,
        weatherLookup,
        decision,
        clientMeta: {
          appVersion: 'weather-bonus-v1',
          decisionComputedAtUtc: new Date().toISOString()
        }
      };

      const response: any = await this.coreService.submitWeatherBonusDecision(requestPayload);
      if (response && response.result && response.result.weatherBonusApplied) {
        return {
          status: 'applied',
          response: response.result,
          debug: {
            stage: 'applied',
            ocrContext: context,
            addressCandidates,
            geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
            geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
            locationResolution,
            weatherRawResponse: this.weatherService.lastDebugResponse,
            weatherLookup,
            decision,
            submissionPayload: requestPayload,
            submissionResponse: response
          }
        };
      }

      return {
        status: 'not_applied',
        response: response && response.result ? response.result : undefined,
        debug: {
          stage: 'not_applied',
          ocrContext: context,
          addressCandidates,
          geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
          geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
          locationResolution,
          weatherRawResponse: this.weatherService.lastDebugResponse,
          weatherLookup,
          decision,
          submissionPayload: requestPayload,
          submissionResponse: response
        }
      };
    } catch (error) {
      return {
        status: 'failed',
        reason: 'weather_bonus_flow_failed',
        debug: {
          stage: 'failed',
          ocrContext: rawContext,
          geocodingAttemptedQuery: this.geocodingService.lastDebug && this.geocodingService.lastDebug.candidate,
          geocodingRawResponse: this.geocodingService.lastDebug && this.geocodingService.lastDebug.response,
          weatherRawResponse: this.weatherService.lastDebugResponse,
          error
        }
      };
    }
  }
}
